import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api, AUCTION_CATEGORIES, asset, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Gavel, Upload, Sparkles, X, Layers, Package, Wand2, Camera } from "lucide-react";

export default function SellItem() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", category: "estate-lot", item_details: "",
    starting_price: "", listing_type: "item", starts_at: "", shop_id: "", shipping_cost: "",
    reserve_price: "", featured: false, bold: false,
  });
  const [images, setImages] = useState([]); // [{path, url}]
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [pricing, setPricing] = useState(false);
  const [priceTip, setPriceTip] = useState(null);
  const [myShops, setMyShops] = useState([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEstate = form.listing_type === "estate";
  const upgradeCount = (form.reserve_price && form.reserve_price !== "0" ? 1 : 0) + (form.featured ? 1 : 0) + (form.bold ? 1 : 0);
  const upgradeFee = upgradeCount * 0.25;

  useEffect(() => {
    api.get("/shops/mine").then(({ data }) => setMyShops([...data.owned, ...data.member])).catch(() => {});
  }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files.slice(0, 12 - images.length)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/auctions/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, { path: data.path, url: data.url }]);
      }
      toast.success("Photos added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const removeImg = (path) => setImages((prev) => prev.filter((i) => i.path !== path));

  const draftWithAI = async () => {
    if (!images.length) return toast.error("Add at least one photo first");
    setDrafting(true);
    try {
      const { data } = await api.post("/auctions/ai-draft", { paths: images.map((i) => i.path), hint: form.title, is_estate: isEstate });
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        description: data.description || f.description,
        category: AUCTION_CATEGORIES.some((c) => c.value === data.category) ? data.category : f.category,
        item_details: data.item_details || f.item_details,
      }));
      toast.success("AI drafted your listing — tweak anything you like");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const suggestPrice = async () => {
    if (!form.title && !form.description) return toast.error("Add a title or description first");
    setPricing(true);
    try {
      const { data } = await api.post("/auctions/ai-price", {
        title: form.title, description: form.description, item_details: form.item_details,
        category: form.category, is_estate: isEstate,
      });
      setPriceTip(data);
      if (data.suggested_starting_price && !form.starting_price) set("starting_price", String(Math.round(data.suggested_starting_price)));
      toast.success("AI suggested a starting price");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't estimate a price");
    } finally {
      setPricing(false);
    }
  };

  const submit = async () => {
    if (!form.title || !form.description || !form.starting_price) {
      return toast.error("Title, description and starting price are required");
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auctions", {
        title: form.title, description: form.description, category: form.category,
        item_details: form.item_details, listing_type: form.listing_type,
        starting_price: Number(form.starting_price),
        shipping_cost: Number(form.shipping_cost) || 0,
        images: images.map((i) => i.url),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        shop_id: form.shop_id || null,
        reserve_price: form.reserve_price ? Number(form.reserve_price) : null,
        featured: !!form.featured,
        bold: !!form.bold,
      });
      toast.success("Your auction is live!");
      navigate(`/auction/${data.auction_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create auction");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
            <Gavel className="h-5 w-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white">List an auction</h1>
            <p className="text-sm text-slate-400">Free to list · 3-day auction · flat 4% fee only when it sells.</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Listing type */}
          <div className="grid grid-cols-2 gap-3">
            <TypeCard active={!isEstate} onClick={() => set("listing_type", "item")} testid="type-item" icon={Package} title="Single item" sub="One product or lot" />
            <TypeCard active={isEstate} onClick={() => set("listing_type", "estate")} testid="type-estate" icon={Layers} title="Estate / Collection" sub="A whole lot sold together" />
          </div>

          {/* Photos first — the easy path */}
          <Field label={isEstate ? "Photos of the estate / collection (add several)" : "Photos (add a few angles)"}>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={onUpload} className="hidden" data-testid="sell-image-file" />
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onUpload} className="hidden" data-testid="sell-camera-file" />
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
              {images.map((img, i) => (
                <div key={img.path} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#121721]">
                  <img src={asset(img.url)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded bg-black/70 text-amber-300">Cover</span>}
                  <button type="button" data-testid={`remove-img-${i}`} onClick={() => removeImg(img.path)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {images.length < 12 && (
                <>
                  <button type="button" data-testid="sell-camera-btn" onClick={() => cameraRef.current?.click()} disabled={uploading}
                    className="aspect-square rounded-lg border-2 border-dashed border-amber-500/40 text-amber-400 hover:border-amber-500 hover:bg-amber-500/5 flex flex-col items-center justify-center gap-1 transition-colors">
                    <Camera className="h-5 w-5" />
                    <span className="text-[11px]">Take photo</span>
                  </button>
                  <button type="button" data-testid="sell-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="aspect-square rounded-lg border-2 border-dashed border-white/15 text-slate-400 hover:border-amber-500/50 hover:text-amber-400 flex flex-col items-center justify-center gap-1 transition-colors">
                    <Upload className="h-5 w-5" />
                    <span className="text-[11px]">{uploading ? "Uploading…" : "Upload"}</span>
                  </button>
                </>
              )}
            </div>
            <Button type="button" data-testid="sell-ai-draft-btn" onClick={draftWithAI} disabled={drafting || !images.length}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full h-11 disabled:opacity-40">
              <Sparkles className="h-4 w-4 mr-1.5" /> {drafting ? "Reading your photos…" : "Draft the whole listing from photos"}
            </Button>
            <p className="text-xs text-slate-500 mt-2">Snap the pile, hit the button, and AI writes the title, description &amp; category — great for big estates.</p>
          </Field>

          <Field label="Title">
            <Input data-testid="sell-title-input" value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder={isEstate ? "Entire garage estate: tools, vintage & collectibles" : "Omega Seamaster 1968, original box"}
              className="bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500" />
          </Field>

          <Field label="Description">
            <Textarea data-testid="sell-description-input" value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="What's included, condition, anything a buyer should know…" rows={5}
              className="bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500 resize-none" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="sell-category-select" className="bg-[#121721] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121721] border-white/10 text-white">
                  {AUCTION_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Details (condition, quantity)">
              <Input data-testid="sell-item-input" value={form.item_details} onChange={(e) => set("item_details", e.target.value)}
                placeholder="~40 items, mixed condition" className="bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500" />
            </Field>
          </div>

          <Field label="Starting price (USD)">
            <div className="flex gap-2">
              <Input data-testid="sell-price-input" type="number" value={form.starting_price} onChange={(e) => set("starting_price", e.target.value)}
                placeholder="50" className="bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500" />
              <Button type="button" data-testid="sell-ai-price-btn" onClick={suggestPrice} disabled={pricing} variant="outline"
                className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full h-10 px-4 shrink-0">
                <Wand2 className="h-4 w-4 mr-1.5" /> {pricing ? "…" : "Suggest"}
              </Button>
            </div>
            {priceTip && (
              <div className="mt-2 text-xs text-slate-400 rounded-lg bg-amber-500/5 border border-amber-500/20 p-3" data-testid="sell-price-tip">
                Suggested start <span className="text-amber-400 font-semibold">{money(priceTip.suggested_starting_price)}</span>
                {priceTip.estimated_value_low ? <> · est. value {money(priceTip.estimated_value_low)}–{money(priceTip.estimated_value_high)}</> : null}
                <div className="mt-1 text-slate-500">{priceTip.reasoning}</div>
              </div>
            )}
          </Field>

          <Field label="Shipping cost the buyer pays (USD) — leave 0 for free shipping or local pickup">
            <Input data-testid="sell-shipping-input" type="number" min="0" value={form.shipping_cost} onChange={(e) => set("shipping_cost", e.target.value)}
              placeholder="0" className="bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500" />
          </Field>

          {/* Paid listing upgrades — 25¢ each, taken from your payout when it sells */}
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3" data-testid="sell-upgrades">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-white flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-400" /> Listing upgrades</div>
              <div className="text-xs text-slate-400">25¢ each · <span className="text-amber-300 font-semibold" data-testid="upgrade-total">{money(upgradeFee)}</span> total</div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="upgrade-reserve-row">
              <input type="checkbox" data-testid="upgrade-reserve" checked={!!form.reserve_price && form.reserve_price !== "0"} onChange={(e) => set("reserve_price", e.target.checked ? String(form.starting_price || "1") : "")} className="mt-1 accent-amber-500 h-4 w-4" />
              <div className="flex-1">
                <div className="text-sm text-white">Reserve price <span className="text-slate-500">(25¢)</span></div>
                <div className="text-xs text-slate-500">A hidden minimum — it won't sell unless bids reach it.</div>
                {!!form.reserve_price && form.reserve_price !== "0" && (
                  <Input data-testid="upgrade-reserve-input" type="number" min="1" value={form.reserve_price} onChange={(e) => set("reserve_price", e.target.value)}
                    placeholder="Your hidden minimum" className="mt-2 h-9 bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500" />
                )}
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="upgrade-featured-row">
              <input type="checkbox" data-testid="upgrade-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="mt-1 accent-amber-500 h-4 w-4" />
              <div>
                <div className="text-sm text-white">Featured boost <span className="text-slate-500">(25¢)</span></div>
                <div className="text-xs text-slate-500">Pin it near the top of the marketplace &amp; Upcoming page.</div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="upgrade-bold-row">
              <input type="checkbox" data-testid="upgrade-bold" checked={form.bold} onChange={(e) => set("bold", e.target.checked)} className="mt-1 accent-amber-500 h-4 w-4" />
              <div>
                <div className="text-sm text-white">Bold &amp; highlighted title <span className="text-slate-500">(25¢)</span></div>
                <div className="text-xs text-slate-500">Make your listing pop in the grid.</div>
              </div>
            </label>

            <p className="text-[11px] text-slate-500">Upgrade fees are only taken from your payout if the item sells — nothing upfront.</p>
          </div>

          {myShops.length > 0 && (
            <Field label="List under a storefront (optional)">
              <Select value={form.shop_id || "none"} onValueChange={(v) => set("shop_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="sell-shop-select" className="bg-[#121721] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121721] border-white/10 text-white">
                  <SelectItem value="none">Just me (no shop)</SelectItem>
                  {myShops.map((s) => <SelectItem key={s.shop_id} value={s.shop_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}

          <Field label="Schedule start (optional — leave blank to go live now)">
            <Input data-testid="sell-schedule-input" type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)}
              className="bg-[#121721] border-white/10 text-white focus-visible:ring-amber-500" />
            <p className="text-xs text-slate-500">Scheduling puts it on the public “Upcoming auctions” page so partners can build hype before it opens.</p>
          </Field>

          <div className="rounded-xl bg-[#121721] border border-white/10 p-4 text-xs text-slate-400">
            If it sells, the platform keeps a flat <span className="text-amber-400 font-semibold">4%</span> and you receive the rest.
            {form.starting_price ? <> On a sale at {money(form.starting_price)} you'd net about <span className="text-white font-semibold">{money(Math.max(0, Number(form.starting_price) * 0.96 - upgradeFee))}</span>{upgradeFee > 0 ? <> (after the 4% fee and {money(upgradeFee)} in upgrades)</> : <> after the 4% fee</>}.</> : null}
          </div>

          <Button data-testid="sell-submit" onClick={submit} disabled={saving}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold h-12 rounded-full text-base">
            {saving ? "Listing…" : form.starts_at ? "Schedule auction" : "List auction now"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-slate-300">{label}</Label>
    {children}
  </div>
);

const TypeCard = ({ active, onClick, testid, icon: Icon, title, sub }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`flex items-center gap-3 text-left rounded-xl border p-4 transition-all ${active ? "border-amber-500/50 bg-amber-500/10" : "border-white/10 bg-[#121721] hover:border-white/20"}`}>
    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-amber-500/20 text-amber-400" : "bg-white/5 text-slate-400"}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-sm font-semibold text-white">{title}</div>
      <div className="text-xs text-slate-500">{sub}</div>
    </div>
  </button>
);
